const { encode, decode } = require('./utils');
const EJSFactory = require('@eurobits/js-factory');

/**
 * Handles memory storage for any application
 * @namespace   e.js
 * @class       e.js.Store
 */
class EJSStore
{
    /**
     * @constructor
     */
    constructor()
    {
        /**
         * Reference to window storage engine provider
         * @property    $_engine
         * @type        {Storage}
         * @default     window.localStorage
         * @private
         * @protected
         */
        Object.defineProperty(
            this,
            '$_engine',
            { value : window && window.localStorage, writable : true }
        );
    }

    /**
     * Sets the new reference for the window engine
     * @setter
     * @param   {Storage|*} ref Reference
     */
    set engine(ref)
    {
        this.$_engine = ref;
    }

    /**
     * Returns the current window Storage engine
     * @getter
     * @return {Storage|*}  The current reference
     */
    get engine()
    {
        return this.$_engine;
    }

    /**
     * Stores a new key-value item
     * @param   {String}    key     Unique value identifier
     * @param   {*}         value   To be stored
     */
    setItem(key = '', value)
    {
        if (key && typeof key === 'string' && !(this[key] instanceof Function) && !key.startsWith('$_'))
        {
            this[key] = value;
        }
    }

    /**
     * Stores a new encoded key-value item
     * @param   {String}    key     Unique value identifier
     * @param   {*}         value   To be stored codified
     */
    setSecItem(key = '', value)
    {
        this.setItem(key, encode(value));
    }

    /**
     * Retrieves an stored valued referenced by its unique key
     * @param   {String}    key Unique identifier
     * @return  {*}         Stored value
     */
    getItem(key = '')
    {
        return this[key];
    }

    /**
     * Retrieves an stored encoded value
     * @param   {String}    key Unique value identifier
     * @return  {String}    Decoded value
     */
    getSecItem(key = '')
    {
        return decode(this.getItem(key));
    }

    /**
     * Removes an stored value referenced by its unique key
     * @param   {String}    key Unique identifier
     */
    removeItem(key = '')
    {
        if (key in this && !(this[key] instanceof Function) && !key.startsWith('$_'))
        {
            delete this[key];
        }
    }

    /**
     * Clears all stored keys
     * @param   {Boolean}   complete    Whether to clear also window Storage
     */
    clear(complete = false)
    {
        Object.keys(this)
            .forEach(this.removeItem.bind(this));

        if (complete)
        {
            this.engine.clear();
        }
    }

    /**
     * Dumps data into window Storage
     */
    dump()
    {
        Object.keys(this)
            .filter(key => !(this[key] instanceof Function))
            .filter(key => !key.startsWith('$_'))
            .forEach(key => this.engine.setItem(key, this.getItem(key)));
    }

    /**
     * Recovers all info from window Storage
     * @param   {RegExp}    pattern     To filter keys, default is /.+/
     */
    recover(pattern = /.+/)
    {
        Object.keys(this.engine)
            .filter(key => pattern.test(key))
            .forEach(key => this.setItem(key, this.engine.getItem(key)));
    }
}

EJSFactory.register('EJSStore', EJSStore);
module.exports = EJSStore;
